import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, RotateCcw, Check, Loader2, Eye } from 'lucide-react';
import {
  CurveChannel,
  CurvePoint,
  CurvesState,
  applyCurvesToImageData,
  buildCurvesLuts,
  buildLut,
  createIdentityCurves,
  isIdentityCurves,
} from '../services/curves';

interface CurvesEditorProps {
  imageUrl: string;
  /** Save the adjusted curves. Parent applies them to the full-res image. */
  onSave: (curves: CurvesState) => void;
  onCancel: () => void;
  isSaving?: boolean;
}

const PREVIEW_MAX = 1600; // longest edge of the live-preview buffer
const GRID = 300; // curve grid canvas size in device px
const HIT_RADIUS = 12; // px tolerance for grabbing a control point

const CHANNELS: { id: CurveChannel; label: string; color: string }[] = [
  { id: 'rgb', label: 'RGB', color: '#e2e8f0' },
  { id: 'r', label: 'Red', color: '#f87171' },
  { id: 'g', label: 'Green', color: '#4ade80' },
  { id: 'b', label: 'Blue', color: '#60a5fa' },
];

const clamp = (v: number, min: number, max: number) =>
  v < min ? min : v > max ? max : v;

const CurvesEditor: React.FC<CurvesEditorProps> = ({
  imageUrl,
  onSave,
  onCancel,
  isSaving = false,
}) => {
  const [curves, setCurves] = useState<CurvesState>(createIdentityCurves);
  const [channel, setChannel] = useState<CurveChannel>('rgb');
  const [selected, setSelected] = useState<number | null>(null);
  const [showBefore, setShowBefore] = useState(false);
  const [histogram, setHistogram] = useState<number[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const gridCanvasRef = useRef<HTMLCanvasElement>(null);
  const baseImageDataRef = useRef<ImageData | null>(null);
  const dragRef = useRef<{ index: number; isEndpoint: boolean } | null>(null);

  const points = curves[channel];
  const activeColor =
    CHANNELS.find((c) => c.id === channel)?.color || '#e2e8f0';

  // --- Load image once, build the downscaled preview buffer + histogram. ---
  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const scale = Math.min(1, PREVIEW_MAX / Math.max(w, h));
      const pw = Math.max(1, Math.round(w * scale));
      const ph = Math.max(1, Math.round(h * scale));

      const off = document.createElement('canvas');
      off.width = pw;
      off.height = ph;
      const offCtx = off.getContext('2d', { willReadFrequently: true });
      if (!offCtx) {
        setLoadError('Could not initialise the editor canvas.');
        return;
      }
      offCtx.drawImage(img, 0, 0, pw, ph);
      let baseData: ImageData;
      try {
        baseData = offCtx.getImageData(0, 0, pw, ph);
      } catch (e) {
        setLoadError('This image could not be loaded for editing (CORS).');
        return;
      }
      baseImageDataRef.current = baseData;

      // Luminance histogram from the same downsample.
      const hist = new Array(256).fill(0);
      const d = baseData.data;
      for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3] < 8) continue;
        const y = Math.round(
          0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]
        );
        hist[clamp(y, 0, 255)]++;
      }
      setHistogram(hist);
      setReady(true);
    };
    img.onerror = () => {
      if (!cancelled) setLoadError('Failed to load the image for editing.');
    };
    img.src = imageUrl;
    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  // --- Render the live preview whenever curves / before toggle change. ---
  const renderPreview = useCallback(() => {
    const base = baseImageDataRef.current;
    const canvas = previewCanvasRef.current;
    if (!base || !canvas) return;
    // The canvas only mounts once `ready` is true, so size it to the preview
    // buffer here (the load handler can't, since the element isn't mounted yet).
    if (canvas.width !== base.width || canvas.height !== base.height) {
      canvas.width = base.width;
      canvas.height = base.height;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (showBefore || isIdentityCurves(curves)) {
      ctx.putImageData(base, 0, 0);
      return;
    }
    const out = new ImageData(
      new Uint8ClampedArray(base.data),
      base.width,
      base.height
    );
    applyCurvesToImageData(out, buildCurvesLuts(curves));
    ctx.putImageData(out, 0, 0);
  }, [curves, showBefore]);

  useEffect(() => {
    if (ready) renderPreview();
  }, [ready, renderPreview]);

  // --- Draw the curve grid (histogram, grid, baseline, spline, points). ---
  useEffect(() => {
    const canvas = gridCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const W = GRID;
    const H = GRID;
    ctx.clearRect(0, 0, W, H);

    // Background
    ctx.fillStyle = '#0b1220';
    ctx.fillRect(0, 0, W, H);

    // Histogram (luminance) behind the grid
    if (histogram.length === 256) {
      const max = Math.max(...histogram) || 1;
      ctx.fillStyle = 'rgba(148, 163, 184, 0.25)';
      for (let i = 0; i < 256; i++) {
        const x = (i / 255) * W;
        const barH = (histogram[i] / max) * H;
        ctx.fillRect(x, H - barH, W / 255 + 0.5, barH);
      }
    }

    // Grid lines (quarters)
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.18)';
    ctx.lineWidth = 1;
    for (let i = 1; i < 4; i++) {
      const p = (i / 4) * W;
      ctx.beginPath();
      ctx.moveTo(p, 0);
      ctx.lineTo(p, H);
      ctx.moveTo(0, p);
      ctx.lineTo(W, p);
      ctx.stroke();
    }

    // Identity diagonal baseline
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.35)';
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(0, H);
    ctx.lineTo(W, 0);
    ctx.stroke();
    ctx.setLineDash([]);

    // The spline curve
    const lut = buildLut(points);
    ctx.strokeStyle = activeColor;
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i < 256; i++) {
      const x = (i / 255) * W;
      const y = H - (lut[i] / 255) * H;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();

    // Control points
    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      const cx = (p.x / 255) * W;
      const cy = H - (p.y / 255) * H;
      ctx.beginPath();
      ctx.arc(cx, cy, i === selected ? 6 : 4.5, 0, Math.PI * 2);
      ctx.fillStyle = i === selected ? activeColor : '#0b1220';
      ctx.strokeStyle = activeColor;
      ctx.lineWidth = 2;
      ctx.fill();
      ctx.stroke();
    }
  }, [points, histogram, selected, activeColor]);

  // --- Pointer interaction on the grid ---
  const toValue = (clientX: number, clientY: number): CurvePoint => {
    const canvas = gridCanvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const cx = ((clientX - rect.left) / rect.width) * GRID;
    const cy = ((clientY - rect.top) / rect.height) * GRID;
    return {
      x: clamp(Math.round((cx / GRID) * 255), 0, 255),
      y: clamp(Math.round((1 - cy / GRID) * 255), 0, 255),
    };
  };

  const updatePoints = (next: CurvePoint[]) => {
    setCurves((prev) => ({ ...prev, [channel]: next }));
  };

  const handleGridPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const v = toValue(e.clientX, e.clientY);
    const canvas = gridCanvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const px = ((v.x / 255) * GRID * rect.width) / GRID;
    const py = (((255 - v.y) / 255) * GRID * rect.height) / GRID;

    // Find an existing point under the cursor (in screen px).
    let hitIndex = -1;
    let bestDist = HIT_RADIUS;
    for (let i = 0; i < points.length; i++) {
      const cx = ((points[i].x / 255) * GRID * rect.width) / GRID;
      const cy = (((255 - points[i].y) / 255) * GRID * rect.height) / GRID;
      const dist = Math.hypot(cx - px, cy - py);
      if (dist <= bestDist) {
        bestDist = dist;
        hitIndex = i;
      }
    }

    let working = [...points];
    if (hitIndex === -1) {
      // Add a new point at this x.
      working.push({ x: v.x, y: v.y });
      working.sort((a, b) => a.x - b.x);
      hitIndex = working.findIndex((p) => p.x === v.x && p.y === v.y);
      updatePoints(working);
    }

    const isEndpoint = hitIndex === 0 || hitIndex === working.length - 1;
    dragRef.current = { index: hitIndex, isEndpoint };
    setSelected(hitIndex);
    canvas.setPointerCapture(e.pointerId);
  };

  const handleGridPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    e.preventDefault();
    const v = toValue(e.clientX, e.clientY);
    setCurves((prev) => {
      const arr = [...prev[channel]];
      const i = drag.index;
      if (!arr[i]) return prev;
      const prevX = i > 0 ? arr[i - 1].x : -1;
      const nextX = i < arr.length - 1 ? arr[i + 1].x : 256;
      let newX = arr[i].x;
      if (i === 0) {
        newX = clamp(v.x, 0, nextX - 1); // black point can slide right
      } else if (i === arr.length - 1) {
        newX = clamp(v.x, prevX + 1, 255); // white point can slide left
      } else {
        newX = clamp(v.x, prevX + 1, nextX - 1);
      }
      arr[i] = { x: newX, y: v.y };
      return { ...prev, [channel]: arr };
    });
  };

  const endDrag = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (dragRef.current) {
      try {
        gridCanvasRef.current?.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    }
    dragRef.current = null;
  };

  const handleGridDoubleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // Remove the nearest non-endpoint point.
    const canvas = gridCanvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * GRID;
    const py = ((e.clientY - rect.top) / rect.height) * GRID;
    let hitIndex = -1;
    let bestDist = HIT_RADIUS;
    for (let i = 0; i < points.length; i++) {
      const cx = (points[i].x / 255) * GRID;
      const cy = ((255 - points[i].y) / 255) * GRID;
      const dist = Math.hypot(cx - px, cy - py);
      if (dist <= bestDist) {
        bestDist = dist;
        hitIndex = i;
      }
    }
    if (hitIndex > 0 && hitIndex < points.length - 1) {
      const arr = points.filter((_, i) => i !== hitIndex);
      updatePoints(arr);
      setSelected(null);
    }
  };

  // Arrow-key nudging for fine, Photoshop-like control of the selected point.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (selected == null) return;
      const arr = curves[channel];
      if (!arr[selected]) return;
      let dx = 0;
      let dy = 0;
      if (e.key === 'ArrowLeft') dx = -1;
      else if (e.key === 'ArrowRight') dx = 1;
      else if (e.key === 'ArrowUp') dy = 1;
      else if (e.key === 'ArrowDown') dy = -1;
      else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selected > 0 && selected < arr.length - 1) {
          updatePoints(arr.filter((_, i) => i !== selected));
          setSelected(null);
          e.preventDefault();
        }
        return;
      } else return;
      e.preventDefault();
      const step = e.shiftKey ? 10 : 1;
      const next = [...arr];
      const i = selected;
      const prevX = i > 0 ? next[i - 1].x : -1;
      const nextX = i < next.length - 1 ? next[i + 1].x : 256;
      let nx = next[i].x + dx * step;
      if (i === 0) nx = clamp(nx, 0, nextX - 1);
      else if (i === next.length - 1) nx = clamp(nx, prevX + 1, 255);
      else nx = clamp(nx, prevX + 1, nextX - 1);
      next[i] = {
        x: nx,
        y: clamp(next[i].y + dy * step, 0, 255),
      };
      updatePoints(next);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, channel, curves]);

  const resetActive = () => {
    setCurves((prev) => ({
      ...prev,
      [channel]: [
        { x: 0, y: 0 },
        { x: 255, y: 255 },
      ],
    }));
    setSelected(null);
  };

  const resetAll = () => {
    setCurves(createIdentityCurves());
    setSelected(null);
  };

  const dirty = useMemo(() => !isIdentityCurves(curves), [curves]);
  const selectedPoint = selected != null ? points[selected] : null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-sm flex flex-col">
      {/* Header */}
      <div className="h-14 flex items-center justify-between px-4 border-b border-slate-800 bg-slate-900/80">
        <div className="flex items-center gap-3">
          <h2 className="text-white font-semibold">Curves</h2>
          <span className="text-xs text-slate-500 hidden sm:inline">
            Click to add points · drag to adjust · double-click to remove ·
            arrow keys for fine control (Shift = ×10)
          </span>
        </div>
        <button
          onClick={onCancel}
          className="p-2 text-slate-400 hover:text-white transition-colors"
          title="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Preview */}
        <div className="flex-1 min-h-0 flex items-center justify-center p-4 overflow-auto">
          {loadError ? (
            <div className="text-center max-w-sm">
              <p className="text-red-400 font-medium mb-2">{loadError}</p>
              <p className="text-slate-500 text-sm">
                Try downloading the image and re-uploading, or contact support.
              </p>
            </div>
          ) : !ready ? (
            <div className="flex flex-col items-center gap-3 text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
              <span className="text-sm">Loading image…</span>
            </div>
          ) : (
            <canvas
              ref={previewCanvasRef}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            />
          )}
        </div>

        {/* Controls */}
        <div className="w-full lg:w-[360px] flex-shrink-0 border-t lg:border-t-0 lg:border-l border-slate-800 bg-slate-900/80 p-4 flex flex-col gap-4 overflow-y-auto">
          {/* Channel tabs */}
          <div className="flex items-center bg-slate-800 rounded-lg p-1">
            {CHANNELS.map((c) => (
              <button
                key={c.id}
                onClick={() => {
                  setChannel(c.id);
                  setSelected(null);
                }}
                className={`flex-1 px-2 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  channel === c.id
                    ? 'bg-slate-700 text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
                style={channel === c.id ? { color: c.color } : undefined}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* Grid */}
          <div className="relative mx-auto" style={{ width: GRID, maxWidth: '100%' }}>
            <canvas
              ref={gridCanvasRef}
              width={GRID}
              height={GRID}
              onPointerDown={handleGridPointerDown}
              onPointerMove={handleGridPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onDoubleClick={handleGridDoubleClick}
              className="w-full rounded-lg border border-slate-700 cursor-crosshair touch-none select-none"
              style={{ aspectRatio: '1 / 1' }}
            />
          </div>

          {/* Selected point readout */}
          <div className="text-xs text-slate-400 text-center h-4">
            {selectedPoint ? (
              <span>
                Input{' '}
                <span className="text-slate-200 font-mono">
                  {selectedPoint.x}
                </span>{' '}
                · Output{' '}
                <span className="text-slate-200 font-mono">
                  {selectedPoint.y}
                </span>
              </span>
            ) : (
              <span className="text-slate-600">No point selected</span>
            )}
          </div>

          {/* Quick presets */}
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() =>
                setCurves((p) => ({
                  ...p,
                  rgb: [
                    { x: 0, y: 0 },
                    { x: 64, y: 52 },
                    { x: 192, y: 204 },
                    { x: 255, y: 255 },
                  ],
                }))
              }
              className="px-2 py-1.5 text-xs rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300"
            >
              Contrast
            </button>
            <button
              onClick={() =>
                setCurves((p) => ({
                  ...p,
                  rgb: [
                    { x: 0, y: 14 },
                    { x: 128, y: 140 },
                    { x: 255, y: 255 },
                  ],
                }))
              }
              className="px-2 py-1.5 text-xs rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300"
            >
              Lift
            </button>
            <button
              onClick={() =>
                setCurves((p) => ({
                  ...p,
                  rgb: [
                    { x: 0, y: 0 },
                    { x: 128, y: 138 },
                    { x: 255, y: 255 },
                  ],
                }))
              }
              className="px-2 py-1.5 text-xs rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300"
            >
              Brighten
            </button>
          </div>

          {/* Reset buttons */}
          <div className="flex gap-2">
            <button
              onClick={resetActive}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Reset {CHANNELS.find((c) => c.id === channel)?.label}
            </button>
            <button
              onClick={resetAll}
              className="px-3 py-2 text-sm rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
            >
              Reset all
            </button>
          </div>

          {/* Before / after */}
          <button
            onMouseDown={() => setShowBefore(true)}
            onMouseUp={() => setShowBefore(false)}
            onMouseLeave={() => setShowBefore(false)}
            onTouchStart={() => setShowBefore(true)}
            onTouchEnd={() => setShowBefore(false)}
            className={`flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
              showBefore
                ? 'bg-orange-500 text-white'
                : 'bg-slate-800 hover:bg-slate-700 text-slate-300'
            }`}
          >
            <Eye className="w-4 h-4" />
            Hold to compare original
          </button>

          <div className="flex-1" />

          {/* Actions */}
          <div className="flex gap-2 pt-2 border-t border-slate-800">
            <button
              onClick={onCancel}
              disabled={isSaving}
              className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-slate-800 hover:bg-slate-700 text-white transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave(curves)}
              disabled={isSaving || !dirty || !ready}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-orange-500 hover:bg-orange-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Applying…
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Apply
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurvesEditor;
